import { EmptyListPlaceholder, SwiperRefType } from "@molecules";
import { forwardRef, useCallback, useMemo } from "react";
import { FlexRow } from "@quarks";
import { StyledCellContainer, StyledSwiperHorizontal } from "./styles";
import Swiper from "swiper";
import { HaNWorkspaceCell } from "../HaNWorkspaceCell";
import { AreaWithSensorInfo } from "@lib/store";
import { useHereAndNow } from "@pages";
import { FloorSuggestionsTile } from "../HaNVerticalListContent/FloorSuggestions/FloorSuggestionsTile";
import { trackEvent } from "@lib/infrastructure";

export type HaNWorkspacesListHorizontalProps = {
  onSlideChange: (room: AreaWithSensorInfo) => void;
  onClick: (area: AreaWithSensorInfo) => void;
};

export const HaNWorkspacesListHorizontal = forwardRef<SwiperRefType, HaNWorkspacesListHorizontalProps>(
  ({ onSlideChange, onClick }, ref) => {
    const { tab, buildingId, filterData, selectedObject } = useHereAndNow();

    const { above, below } = filterData.workspaces.adjacentFilteredData;

    const aboveFloorId = above.at(0)?.floorId || null;
    const belowFloorId = below.at(0)?.floorId || null;

    const listData = useMemo(() => filterData.workspaces.filteredData, [filterData.workspaces.filteredData]);

    const handleSlideChange = (swiper: Swiper) => {
      const visibleWorkspace = listData[swiper.activeIndex];
      if (!visibleWorkspace) return;
      onSlideChange(visibleWorkspace);
    };

    const scrollToSelected = useCallback(
      (swiper: Swiper) => {
        const index = listData.findIndex(room => room.id === selectedObject?.id);
        if (index !== -1) {
          swiper.slideTo(index, 0);
        }
      },
      [listData, selectedObject?.id],
    );

    return (
      <FlexRow
        zIndex={2}
        columnGap={8}
        overflow="hidden"
        justifyContent="center">
        {listData.length || aboveFloorId || belowFloorId ? (
          <StyledSwiperHorizontal
            freeMode={false}
            onSlideChange={handleSlideChange}
            ref={ref}
            onUpdate={scrollToSelected}
            centeredSlides={true}
            spaceBetween={16}>
            {listData.length > 0 ? (
              listData.map((area, index) => (
                <StyledCellContainer key={area.id}>
                  <HaNWorkspaceCell
                    onClick={onClick}
                    key={area.id}
                    area={area}
                  />
                </StyledCellContainer>
              ))
            ) : (
              <EmptyListPlaceholder className="p-2 justify-center [&>svg]:size-14" />
            )}

            {aboveFloorId && (
              <StyledCellContainer>
                <FloorSuggestionsTile
                  onClick={() => {
                    if (ref && "current" in ref && ref.current) {
                      ref.current.scrollToSlide(0);
                    }
                  }}
                  floorId={aboveFloorId}
                  direction={"up"}
                />
              </StyledCellContainer>
            )}

            {above.map((area, index) => (
              <StyledCellContainer key={area.id}>
                <HaNWorkspaceCell
                  onClick={e => {
                    onClick(e);
                    trackEvent("HereAndNow_OtherFloorSuggestions__SuggestionClick", {
                      buildingId,
                      tab,
                      floorId: aboveFloorId || "",
                      type: "above",
                      nodeId: area.id,
                    });
                  }}
                  key={area.id}
                  area={area}
                />
              </StyledCellContainer>
            ))}

            {belowFloorId && (
              <StyledCellContainer>
                <FloorSuggestionsTile
                  onClick={() => {
                    if (ref && "current" in ref && ref.current) {
                      ref.current.scrollToSlide(0);
                    }
                  }}
                  floorId={belowFloorId}
                  direction={"down"}
                />
              </StyledCellContainer>
            )}

            {below.map((area, index) => (
              <StyledCellContainer key={area.id}>
                <HaNWorkspaceCell
                  onClick={e => {
                    onClick(e);
                    trackEvent("HereAndNow_OtherFloorSuggestions__SuggestionClick", {
                      buildingId,
                      tab,
                      floorId: belowFloorId || "",
                      type: "below",
                      nodeId: area.id,
                    });
                  }}
                  key={area.id}
                  area={area}
                />
              </StyledCellContainer>
            ))}
          </StyledSwiperHorizontal>
        ) : (
          <EmptyListPlaceholder />
        )}
      </FlexRow>
    );
  },
);
